// Koło nastroju przed lekcją (patch-32): „Jaki masz dziś nastrój?”. Dziecko kręci kołem (przeciąga palcem albo myszą — koło kręci się z rozpędem i zatrzymuje na wycinku
// pod wskaźnikiem), dotyka wycinka albo używa strzałek; u góry widać wybraną buźkę i jej nazwę (tylko w języku bota: EN albo PL, z końcówką wg płci dziecka).
// To nie losowanie: wynik zawsze wybiera dziecko, koło tylko „czuje się” jak ruletka. Bot niczego tu nie czyta (żadnych nagrań nastrojów).
// Nic się nie zapisuje samo: przycisk „Let's go! / Zaczynamy!” zapisuje wybrany nastrój i dopiero wtedy startuje lekcja; do pierwszego wyboru jest wyszarzony
// (żeby nie zapisać domyślnej buźki bez namysłu). Nie ma „Pomiń”. Raz dziennie: ekran pokazuje się przed lekcją, dopóki nie ma wpisu na dziś (`shouldAskMood`).
import { DEFAULT_MOOD, MOODS, moodImage, moodName, setMood } from '../lib/mood.ts';
import { angleFor, easeOutCubic, edgesCrossed, indexAt, mod, norm180, pointerAngle, releaseVelocity, snapAngle, stepIndex, stepOf, stepSpin, travelMs } from '../lib/moodWheel.ts';
import type { AngleSample } from '../lib/moodWheel.ts';
import type { Pair } from '../lib/i18n.ts';
import type { TeacherLang } from '../types.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import { genderOf } from './forms.ts';
import { langOf, onLang, tr, tx, txAttr, txText } from './i18n.ts';
import { renderLangToggle } from './langToggle.ts';
import { icon } from './kit.ts';
import { renderProfileBar } from './profileBar.ts';

export interface MoodOptions {
  /** Zapisano (przycisk „Zaczynamy!”) — zaczynamy lekcję. */
  onDone(): void;
  /** „<” — wraca na ekran główny, nic się nie zapisuje i lekcja się nie zaczyna. */
  onBack(): void;
}

/** Napisy ekranu w obu językach bota (nigdy oba naraz). */
const T = {
  title: { pl: 'Jaki masz dziś nastrój?', en: 'How are you feeling today?' },
  sub: { pl: 'Zakręć kołem i wybierz, jak się czujesz dzisiaj.', en: 'Spin the wheel and pick how you feel today.' },
  go: { pl: 'Zaczynamy!', en: "Let's go!" },
  back: { pl: 'Wróć', en: 'Back' },
  wheel: { pl: 'Nastrój — zakręć kołem', en: 'Mood — spin the wheel' },
} as const satisfies Record<string, Pair>;

type Styled = { style?: { setProperty?: (k: string, v: string) => void } };
/** Ustawia zmienną CSS; w teście na atrapie DOM (bez `style`) nic nie robi. */
const setVar = (el: unknown, name: string, value: string): void => (el as Styled).style?.setProperty?.(name, value);
const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** „Ząbki” ruletki: króciutkie stuknięcie (WebAudio) i delikatna wibracja przy każdym przejściu przez granicę wycinka. Wszystko opcjonalne — bez API po prostu cisza. */
function createTicker(): { arm(): void; tick(): void } {
  let ac: AudioContext | null = null;
  let last = 0;
  return {
    arm() {
      if (ac || typeof AudioContext === 'undefined') return;
      try {
        ac = new AudioContext();
      } catch {
        ac = null;
      }
    },
    tick() {
      const t = now();
      if (t - last < 40) return; // przy dużej prędkości nie robimy „bzyczenia”
      last = t;
      try {
        if (typeof navigator !== 'undefined') navigator.vibrate?.(5);
      } catch {
        /* wibracja niedostępna */
      }
      if (!ac) return;
      try {
        if (ac.state === 'suspended') void ac.resume();
        const osc = ac.createOscillator();
        const gain = ac.createGain();
        const t0 = ac.currentTime;
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(1500, t0);
        osc.frequency.exponentialRampToValueAtTime(700, t0 + 0.03);
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.045, t0 + 0.004);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.04);
        osc.connect(gain).connect(ac.destination);
        osc.start(t0);
        osc.stop(t0 + 0.05);
      } catch {
        /* brak dźwięku „ząbków” nie psuje wyboru */
      }
    },
  };
}

export function renderMood(ctx: AppCtx, opts: MoodOptions): void {
  const n = MOODS.length;
  const step = stepOf(n);
  const startIndex = Math.max(0, MOODS.findIndex((m) => m.id === DEFAULT_MOOD));
  let angle = angleFor(startIndex, n);
  let selected = startIndex;
  let touched = false; // dziecko coś wybrało (zakręciło, dotknęło buźki) — dopiero wtedy działa „Zaczynamy!”
  let raf = 0;
  let pending: number | null = null; // cel trwającego przesunięcia (strzałki/kółko myszy): szybkie kolejne kroki liczą od celu, nie od buźki w połowie drogi
  let off: (() => void) | undefined;
  let gen = 0; // numer „ruchu”: nowy ruch unieważnia dokończenie poprzedniego
  const { arm, tick } = createTicker();
  const lang = (): TeacherLang => langOf(ctx);
  const gender = genderOf(ctx);

  // --- wycinki koła -------------------------------------------------------------------------------
  const slots = MOODS.map((m, i) => {
    const img = h('img', { class: 'wslot-img', src: moodImage(m.id), alt: '', draggable: 'false', decoding: 'async' });
    const slot = h('div', { class: 'wslot', 'data-mood': m.id, 'data-testid': `mood-slot-${m.id}` }, img);
    setVar(slot, '--s', String(i * step));
    return slot;
  });
  const disc = h('div', { class: 'wheel-disc', 'data-testid': 'mood-disc' }, ...slots);
  const colors = MOODS.map((m, i) => `${m.color} ${i * step}deg ${(i + 1) * step}deg`).join(', ');
  const discStyle = (disc as unknown as { style?: { background?: string } }).style;
  if (discStyle) discStyle.background = `repeating-conic-gradient(from ${-step / 2 - 0.35}deg, #fff 0deg 0.7deg, transparent 0.7deg ${step}deg), conic-gradient(from ${-step / 2}deg, ${colors})`;
  const pointer = h('div', { class: 'wheel-pointer', 'aria-hidden': 'true' });
  const hub = h('div', { class: 'wheel-hub', 'aria-hidden': 'true' });
  const rot = h('div', { class: 'wheel-rot' }, disc, hub, pointer);
  const wheel = h('div', { class: 'wheel', role: 'slider', tabindex: '0', 'aria-label': tr(ctx, T.wheel), 'aria-valuemin': '1', 'aria-valuemax': String(n), 'data-testid': 'mood-wheel' }, rot);

  // --- podgląd wybranej buźki, nazwa i „Zaczynamy!” ---------------------------------------------------
  const face = h('img', { class: 'mood-face-img', src: moodImage(MOODS[selected]!.id), alt: '', draggable: 'false', 'data-testid': 'mood-face-img' });
  const faceBox = h('div', { class: 'mood-face', 'data-testid': 'mood-face' }, face);
  const pill = h('div', { class: 'mood-pill', 'data-testid': 'mood-name' }, moodName(MOODS[selected]!, lang(), gender));
  const go = txText(ctx, button('', () => commit(), { class: 'btn primary mood-go', testid: 'mood-go', disabled: true }), T.go);
  const pick = h('div', { class: 'mood-pick' }, faceBox, pill, go);

  const back = button(icon('back'), () => { leave(); opts.onBack(); }, { class: 'icon-btn', testid: 'mood-back' });
  txAttr(ctx, back, 'title', T.back);
  txAttr(ctx, back, 'aria-label', T.back);

  // --- stan i ruch ----------------------------------------------------------------------------------
  const paintSelection = (): void => {
    const m = MOODS[selected]!;
    setVar(pick, '--mc', m.color);
    face.setAttribute('src', moodImage(m.id));
    pill.textContent = moodName(m, lang(), gender);
    wheel.setAttribute('data-mood', m.id);
    wheel.setAttribute('aria-valuenow', String(selected + 1));
    wheel.setAttribute('aria-valuetext', moodName(m, lang(), gender));
  };
  const apply = (prev: number): void => {
    setVar(wheel, '--a', angle.toFixed(2));
    const idx = indexAt(angle, n);
    if (idx !== selected) {
      selected = idx;
      paintSelection();
    }
    if (edgesCrossed(prev, angle, n) > 0) tick();
  };
  const markTouched = (): void => {
    if (touched) return;
    touched = true;
    (go as HTMLButtonElement).disabled = false;
  };
  /** Zapisuje wybrany nastrój i zaczyna lekcję („Zaczynamy!”, Enter na kole po wyborze). */
  const commit = (): void => {
    if (!touched) return;
    const m = MOODS[pending ?? indexAt(angle, n)]!; // klik w czasie ruchu bierze buźkę, do której koło jedzie
    leave();
    ctx.setProgress(setMood(ctx.getProgress(), ctx.today(), m.id, Date.now()));
    opts.onDone();
  };
  /** Wybór „stoi”: dziecko dotknęło piasty — bierzemy to, co widać. */
  const choose = (): void => {
    stop();
    markTouched();
  };
  const settle = (): void => {
    pending = null;
    selected = indexAt(angle, n);
    paintSelection();
    markTouched();
    faceBox.classList.remove('pop');
    void (faceBox as HTMLElement).offsetWidth;
    faceBox.classList.add('pop');
  };
  /** Każdy nowy ruch unieważnia trwającą animację. */
  const stop = (): void => {
    gen++;
    if (raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    raf = 0;
  };
  const leave = (): void => {
    stop();
    off?.();
  };
  const canAnimate = (): boolean => typeof requestAnimationFrame === 'function' && !(typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);

  /** Dosuwa/przewozi koło do obrotu `target` (easeOut), potem `settle`. Bez animacji (testy, „mniej ruchu”) przeskakuje od razu. */
  const animateTo = (target: number): void => {
    stop();
    const my = gen;
    if (!canAnimate()) {
      const prev = angle;
      angle = target;
      apply(prev);
      settle();
      return;
    }
    const from = angle;
    const ms = travelMs(target - from);
    const t0 = now();
    const frame = (): void => {
      if (my !== gen) return;
      const k = Math.min(1, (now() - t0) / ms);
      const prev = angle;
      angle = from + (target - from) * easeOutCubic(k);
      apply(prev);
      if (k < 1) raf = requestAnimationFrame(frame);
      else {
        raf = 0;
        settle();
      }
    };
    raf = requestAnimationFrame(frame);
  };
  const snap = (): void => animateTo(snapAngle(angle, n));
  const goTo = (idx: number): void => {
    pending = idx;
    animateTo(angleFor(idx, n, angle));
  };

  /** Rozpęd po puszczeniu palca: tarcie wytraca prędkość, a gdy zwolni — koło dosuwa się do najbliższego wycinka. */
  const coast = (v0: number): void => {
    stop();
    if (!canAnimate()) return snap();
    const my = gen;
    let v = v0;
    let last = now();
    const frame = (): void => {
      if (my !== gen) return;
      const t = now();
      const prev = angle;
      const r = stepSpin(angle, v, Math.min(48, t - last));
      last = t;
      angle = r.angle;
      v = r.v;
      apply(prev);
      if (v !== 0) raf = requestAnimationFrame(frame);
      else {
        raf = 0;
        snap();
      }
    };
    raf = requestAnimationFrame(frame);
  };

  // --- wskaźnik palca ---------------------------------------------------------------------------------
  const centerOf = (el: Element): { x: number; y: number; w: number } => {
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width };
  };
  let drag: { id: number; lastA: number; samples: AngleSample[]; x0: number; y0: number; t0: number; moved: number } | null = null;

  wheel.addEventListener('pointerdown', (e) => {
    const ev = e as PointerEvent;
    stop();
    pending = null;
    arm();
    const c = centerOf(rot);
    drag = { id: ev.pointerId, lastA: pointerAngle(ev.clientX, ev.clientY, c.x, c.y), samples: [{ t: now(), a: angle }], x0: ev.clientX, y0: ev.clientY, t0: now(), moved: 0 };
    wheel.setPointerCapture?.(ev.pointerId);
    ev.preventDefault?.();
  });
  wheel.addEventListener('pointermove', (e) => {
    const ev = e as PointerEvent;
    if (!drag || ev.pointerId !== drag.id) return;
    const c = centerOf(rot);
    const a = pointerAngle(ev.clientX, ev.clientY, c.x, c.y);
    const delta = norm180(a - drag.lastA);
    drag.lastA = a;
    drag.moved += Math.hypot(ev.clientX - drag.x0, ev.clientY - drag.y0) > 8 ? Math.abs(delta) : 0;
    const prev = angle;
    angle += delta;
    apply(prev);
    drag.samples.push({ t: now(), a: angle });
    if (drag.samples.length > 12) drag.samples.shift();
  });
  const release = (e: Event): void => {
    const ev = e as PointerEvent;
    if (!drag || ev.pointerId !== drag.id) return;
    const d = drag;
    drag = null;
    const dist = Math.hypot(ev.clientX - d.x0, ev.clientY - d.y0);
    if (dist < 10 && now() - d.t0 < 450) {
      // dotknięcie: wycinek pod palcem przyjeżdża pod wskaźnik; dotknięcie piasty wybiera to, co widać
      const c = centerOf(rot);
      const radius = Math.hypot(ev.clientX - c.x, ev.clientY - c.y);
      if (radius < c.w * 0.28) {
        choose();
        return;
      }
      const p = centerOf(pointer);
      const base = pointerAngle(p.x, p.y, c.x, c.y);
      const rel = pointerAngle(ev.clientX, ev.clientY, c.x, c.y) - base;
      goTo(mod(Math.round((rel - angle) / step), n));
      return;
    }
    const v = releaseVelocity(d.samples);
    if (Math.abs(v) > 90) coast(v);
    else snap();
  };
  wheel.addEventListener('pointerup', release);
  wheel.addEventListener('pointercancel', release);

  // kółko myszy na komputerze: jeden „ząbek” = jedna buźka (z krótką przerwą, żeby gładka touchpadowa przewijanka nie przeskakiwała kilku naraz)
  let wheelAt = 0;
  wheel.addEventListener('wheel', (e) => {
    const ev = e as WheelEvent;
    ev.preventDefault?.();
    arm();
    const t = now();
    if (t - wheelAt < 160) return;
    const d = Math.abs(ev.deltaY) >= Math.abs(ev.deltaX) ? ev.deltaY : ev.deltaX;
    if (!d) return;
    wheelAt = t;
    goTo(stepIndex(pending ?? selected, d > 0 ? 1 : -1, n));
  });

  wheel.addEventListener('keydown', (e) => {
    const ev = e as KeyboardEvent;
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault?.();
      if (touched) commit();
      else choose();
      return;
    }
    const dir = ev.key === 'ArrowRight' || ev.key === 'ArrowDown' ? 1 : ev.key === 'ArrowLeft' || ev.key === 'ArrowUp' ? -1 : 0;
    if (dir === 0) return;
    ev.preventDefault?.();
    arm();
    goTo(stepIndex(pending ?? selected, dir, n));
  });

  // --- ekran ------------------------------------------------------------------------------------------
  setVar(wheel, '--a', String(angle));
  paintSelection();
  const titleText = tx(ctx, T.title, 'mood-title-text');
  const title = h('h1', { class: 'mood-title', 'data-testid': 'mood-title' }, titleText, ' ', h('span', { class: 'mood-spark', 'aria-hidden': 'true' }));
  const sub = txText(ctx, h('p', { class: 'mood-sub', 'data-testid': 'mood-sub' }), T.sub);
  const toggle = renderLangToggle(ctx);
  txAttr(ctx, wheel, 'aria-label', T.wheel);
  // zmiana języka (EN ⌄) przepisuje napisy na ekranie od razu (tytuł, podtytuł, przyciski i etykiety same; nazwa wybranej buźki tutaj)
  off = onLang(ctx, () => paintSelection());
  // pasek u góry, poza kartą (jak na ekranie lekcji): ← | profil | EN
  const topbar = h('header', { class: 'topbar mw-topbar', 'data-testid': 'mood-topbar' }, h('div', { class: 'tb-left' }, back), h('div', { class: 'tb-mid' }, renderProfileBar(ctx)), toggle);
  ctx.root.replaceChildren(
    topbar,
    h('main', { class: 'stage mood', 'data-testid': 'mood-screen' },
      h('div', { class: 'mood-body' },
        h('div', { class: 'mood-copy' },
          title,
          sub,
          pick,
        ),
        h('div', { class: 'mood-wheel-area' }, wheel),
      ),
    ),
  );
}
